import { Injectable, computed, signal, effect, inject, DestroyRef } from '@angular/core';

export interface ImpState {
  on: boolean;
  targetUserId?: string;
  actor?: { id: string; name?: string };
  expUnixSec?: number;
}

@Injectable({ providedIn: 'root' })
export class ImpersonationService {
  private destroyRef = inject(DestroyRef);

  private stateSig = signal<ImpState>({ on: false });
  private now = signal<number>(Date.now()); // ms
  private tickHandle: ReturnType<typeof setInterval> | null = null;


  isOn = computed(() => this.stateSig().on);
  targetUserId = computed(() => this.stateSig().targetUserId ?? '—');
  actorLabel = computed(() => {
    const a = this.stateSig().actor; return a?.name || a?.id || '—';
  });


  remaining = computed(() => {
    const exp = this.stateSig().expUnixSec;

    void this.now();

    if (!this.stateSig().on || !exp) return '—';

    const sec = Math.max(0, Math.floor(exp - Date.now() / 1000));
    const mm = Math.floor(sec / 60).toString().padStart(2, '0');
    const ss = (sec % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  });

    remainingSec = computed(() => {
    this.now();
    const exp = this.stateSig().expUnixSec ?? 0;
    return Math.max(0, Math.floor(exp - Date.now() / 1000));
  });


  private ticker = effect(() => {
    const on = this.isOn();
    this.stopTick();

    if (on) {
      this.now.set(Date.now());
      this.tickHandle = setInterval(() => this.now.set(Date.now()), 1000);
    }
  });

  private stopTick() {
    if (this.tickHandle) {
      clearInterval(this.tickHandle);
      this.tickHandle = null;
    }
  }

  constructor() {
    this.destroyRef.onDestroy(() => this.stopTick());
  }


  updateFromHeaders(h: Headers | Record<string, string | null | undefined>) {
    const get = (k: string) =>
      h instanceof Headers ? h.get(k) : (h[k.toLowerCase()] ?? h[k] ?? null);

    const imp = (get('X-Imp') || '').toLowerCase() === 'true';
    if (!imp) { this.stateSig.set({ on: false }); return; }

    const sub = get('X-Imp-Sub') ?? undefined;
    const act = get('X-Imp-Act');
    const exp = get('X-Imp-Exp');

    this.stateSig.set({
      on: true,
      targetUserId: sub || undefined,
      actor: safeParse<{ id: string; name?: string }>(act || undefined),
      expUnixSec: exp ? Number(exp) : undefined,
    });

    function safeParse<T>(s?: string | null): T | undefined {
      if (!s) return undefined;
      try { return JSON.parse(s) as T; } catch { return undefined; }
    }
  }

  clear() { this.stateSig.set({ on: false }); }

  set(state: ImpState) { this.stateSig.set(state); }
}
